import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton,
  ListItemText, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { request, useOperations } from './OperationsProvider';
import { loadGoogle } from './GoogleOperationsMap';
import { findPlaces } from './placeSearch';

// Where a stage begins and ends on the ground.
//
// A circle is the right fence for a yard or a customer site. Inside a plant it is usually the wrong
// one: a weighbridge lane, a gate road or a line of unloading bays is long and narrow, and a circle
// wide enough to cover the length of one also covers the lane beside it and the road behind it - so
// the truck in the next lane is recorded at this weighbridge, and the stage timings that the whole
// turnaround figure rests on are quietly wrong. This draws the lane instead: a line down the middle
// of it, and how far either side still counts as being on it.
//
// Everything is drawn on the satellite view, because that is where somebody can see the lane they
// are fencing rather than guess at coordinates.
const LANE_COLOUR = '#0069ed';
const DEFAULT_CORRIDOR = 20;
const DEFAULT_RADIUS = 80;

const toLatLng = (point) => ({ lat: point.lat, lng: point.lon });

// The corridor, drawn so it can be seen: a band either side of each run of the line, and a disc at
// each bend so the corners are not left open. Google has no buffer of its own, and an approximation
// somebody can see beats an exact one they cannot.
const corridorShapes = (maps, map, points, metres) => {
  const shapes = [];
  const style = {
    map, clickable: false, strokeWeight: 0, fillColor: LANE_COLOUR, fillOpacity: 0.16,
  };
  points.forEach((point) => shapes.push(new maps.Circle({ ...style, center: toLatLng(point), radius: metres })));
  for (let i = 1; i < points.length; i += 1) {
    const from = points[i - 1];
    const to = points[i];
    const scale = Math.cos((from.lat * Math.PI) / 180);
    const dx = (to.lon - from.lon) * scale * 111320;
    const dy = (to.lat - from.lat) * 110540;
    const length = Math.hypot(dx, dy);
    // Two points in the same place are one point; the disc already drawn covers them.
    if (length >= 0.5) {
      // Perpendicular to the run, in metres, converted back into degrees at this latitude.
      const dLon = ((-dy / length) * metres) / (scale * 111320);
      const dLat = ((dx / length) * metres) / 110540;
      shapes.push(new maps.Polygon({
        ...style,
        paths: [
          { lat: from.lat + dLat, lng: from.lon + dLon },
          { lat: to.lat + dLat, lng: to.lon + dLon },
          { lat: to.lat - dLat, lng: to.lon - dLon },
          { lat: from.lat - dLat, lng: from.lon - dLon },
        ],
      }));
    }
  }
  return shapes;
};

const CheckpointFence = ({ checkpoint, onClose }) => {
  const { data, refresh } = useOperations();
  const [config, setConfig] = useState(null);
  const [mode, setMode] = useState(checkpoint.shape === 'POLYLINE' ? 'LANE' : 'CIRCLE');
  const [corridor, setCorridor] = useState(checkpoint.corridorMeters || DEFAULT_CORRIDOR);
  const [radius, setRadius] = useState(checkpoint.radiusMeters || DEFAULT_RADIUS);
  const [points, setPoints] = useState(checkpoint.path || []);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const host = useRef(null); const mapRef = useRef(null); const api = useRef(null);
  const shape = useRef(null); const band = useRef([]); const others = useRef([]); const framed = useRef(false);

  useEffect(() => { request('/api/operations/maps').then(setConfig).catch(() => setError('Map configuration is unavailable.')); }, []);

  // Where to open. The old rule averaged every placed checkpoint, which on a tenant running two
  // works puts the view on farmland between them, and showed the whole subcontinent when there were
  // none - leaving somebody to find a weighbridge by panning.
  //
  // The site is known before anything is placed: a checkpoint carries a site name, the area drawn
  // for that site carries the same one, and every area stores a centre so a map can frame it. So
  // the start is the smallest thing actually known about this stage, with the zoom that matches how
  // precisely it is known. A guess is not opened at survey zoom, and a certainty is not opened at
  // the scale of a country.
  const at = (lat, lng, zoom) => ({ centre: { lat: Number(lat), lng: Number(lng) }, zoom });
  const hasPoint = (r) => r && r.latitude != null && r.longitude != null;
  const onThisSite = (r) => !!checkpoint.site && r.site === checkpoint.site;
  const areas = data.areas || [];
  const placed = (data.checkpoints || []).filter(hasPoint);

  const opening = (() => {
    if (hasPoint(checkpoint)) return at(checkpoint.latitude, checkpoint.longitude, 18);
    const siteArea = areas.find((a) => onThisSite(a) && hasPoint(a));
    if (siteArea) return at(siteArea.latitude, siteArea.longitude, 16);
    const neighbour = placed.find((c) => onThisSite(c) && c.id !== checkpoint.id);
    if (neighbour) return at(neighbour.latitude, neighbour.longitude, 17);
    // No area carries this site's name, but one may be named after it - a plant that drew
    // "Anchepalya Works boundary" without filling the site field in.
    const named = checkpoint.site
      ? areas.find((a) => hasPoint(a) && a.name?.toLowerCase().includes(checkpoint.site.toLowerCase()))
      : null;
    if (named) return at(named.latitude, named.longitude, 16);
    if (placed.length === 1) return at(placed[0].latitude, placed[0].longitude, 16);
    const anyArea = areas.find(hasPoint);
    if (anyArea) return at(anyArea.latitude, anyArea.longitude, 14);
    // Nothing placed and nothing drawn. The country, and the search box above the map.
    return at(20.59, 78.96, 5);
  })();
  const { centre } = opening;

  useEffect(() => {
    if (!config?.googleMapsApiKey) return undefined;
    let cancelled = false;
    loadGoogle(config.googleMapsApiKey).then(async (maps) => {
      const { Map } = await maps.importLibrary('maps');
      if (cancelled) return;
      api.current = maps;
      mapRef.current = new Map(host.current, {
        center: centre,
        zoom: opening.zoom,
        mapId: config.googleMapId || 'DEMO_MAP_ID',
        mapTypeId: 'hybrid',
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      });
      // In lane mode a click adds the next point, which is how anybody expects to draw a line.
      mapRef.current.addListener('click', (event) => {
        setPoints((current) => [...current, { lat: event.latLng.lat(), lon: event.latLng.lng() }]);
      });
      setReady(true);
    }).catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [config]);

  // The stages around this one stay visible but inert, so a lane can be drawn against its neighbours.
  useEffect(() => {
    if (!ready) return;
    others.current.forEach((o) => o.setMap(null));
    const maps = api.current;
    others.current = (data.checkpoints || []).filter((c) => c.id !== checkpoint.id && c.latitude != null).flatMap((c) => (
      c.shape === 'POLYLINE' && (c.path || []).length > 1
        ? [new maps.Polyline({
          map: mapRef.current, path: c.path.map(toLatLng), clickable: false, strokeColor: '#9aa4b2', strokeWeight: 3, strokeOpacity: 0.9,
        })]
        : c.radiusMeters > 0
          ? [new maps.Circle({
            map: mapRef.current, center: { lat: c.latitude, lng: c.longitude }, radius: c.radiusMeters, clickable: false, strokeColor: '#9aa4b2', strokeWeight: 1, fillColor: '#9aa4b2', fillOpacity: 0.08,
          })]
          : []
    ));
  }, [ready, data.checkpoints, checkpoint.id]);

  // One editable overlay, redrawn whenever the shape being edited changes.
  const draw = useCallback(() => {
    if (!ready) return;
    const maps = api.current;
    shape.current?.setMap(null); shape.current = null;
    band.current.forEach((b) => b.setMap(null)); band.current = [];
    if (mode === 'LANE') {
      if (!points.length) return;
      band.current = corridorShapes(maps, mapRef.current, points, Number(corridor) || DEFAULT_CORRIDOR);
      shape.current = new maps.Polyline({
        map: mapRef.current, path: points.map(toLatLng), editable: true, strokeColor: LANE_COLOUR, strokeWeight: 3,
      });
      // Dragging a point moves the lane; right-clicking one removes it, down to the two a line needs.
      const read = () => {
        const path = shape.current.getPath();
        const next = [];
        for (let i = 0; i < path.getLength(); i += 1) next.push({ lat: path.getAt(i).lat(), lon: path.getAt(i).lng() });
        setPoints(next);
      };
      ['set_at', 'insert_at', 'remove_at'].forEach((event) => shape.current.getPath().addListener(event, read));
      shape.current.addListener('rightclick', (event) => {
        if (event.vertex == null || shape.current.getPath().getLength() <= 2) return;
        shape.current.getPath().removeAt(event.vertex);
      });
      if (points.length > 1 && !framed.current) {
        framed.current = true;
        const bounds = new maps.LatLngBounds();
        points.forEach((p) => bounds.extend(toLatLng(p)));
        mapRef.current.fitBounds(bounds, 80);
      }
      return;
    }
    shape.current = new maps.Circle({
      map: mapRef.current,
      center: checkpoint.latitude != null ? { lat: checkpoint.latitude, lng: checkpoint.longitude } : mapRef.current.getCenter(),
      radius: Number(radius) || DEFAULT_RADIUS,
      editable: true,
      draggable: true,
      strokeColor: LANE_COLOUR,
      fillColor: LANE_COLOUR,
      fillOpacity: 0.16,
      strokeWeight: 2,
    });
    shape.current.addListener('radius_changed', () => setRadius(Math.round(shape.current.getRadius())));
  }, [ready, mode, points, corridor, radius === null]);

  useEffect(draw, [ready, mode, points, corridor]);

  // Moves the view and nothing else. A geocoder's point for a works is near its office, so placing
  // the fence from a search result would put a weighbridge somewhere plausible and wrong - and a
  // wrong fence reads as a right one.
  const look = async (event) => {
    event.preventDefault();
    setSearching(true); setError(''); setResults(null);
    try {
      const found = await findPlaces(query, api.current);
      setResults(found);
      if (!found.length) setError(`Nothing found for "${query}". Try the site name without its company suffix, or a nearby landmark.`);
    } catch (e) {
      setError(e.message);
    } finally { setSearching(false); }
  };

  const goTo = (place) => {
    if (!mapRef.current) return;
    if (place.bounds) {
      mapRef.current.fitBounds(place.bounds);
      // A viewport for a whole works is too wide to draw a gate on; one for an address is about
      // right. Capped rather than used as given.
      if (mapRef.current.getZoom() > 18) mapRef.current.setZoom(18);
    } else {
      mapRef.current.setCenter({ lat: place.latitude, lng: place.longitude });
      mapRef.current.setZoom(17);
    }
    setResults(null);
  };

  const save = async () => {
    setPending(true); setError('');
    try {
      const body = mode === 'LANE'
        ? { path: points, corridorMeters: Number(corridor) }
        : {
          latitude: shape.current?.getCenter().lat(),
          longitude: shape.current?.getCenter().lng(),
          radiusMeters: Math.round(shape.current?.getRadius() || Number(radius)),
        };
      await request(`/api/operations/checkpoints/${checkpoint.id}/fence`, 'PATCH', body);
      await refresh();
      onClose();
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const laneLength = points.reduce((total, point, index) => {
    if (!index) return 0;
    const previous = points[index - 1];
    const scale = Math.cos((previous.lat * Math.PI) / 180);
    return total + Math.hypot((point.lon - previous.lon) * scale * 111320, (point.lat - previous.lat) * 110540);
  }, 0);

  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !pending && onClose()}>
      <DialogTitle>{`Geofence · ${checkpoint.name}`}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Paper component="form" onSubmit={look} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" gap={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Find the site"
                placeholder={checkpoint.site ? `e.g. ${checkpoint.site}` : 'Plant, depot, address or landmark'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <Button type="submit" variant="outlined" startIcon={<SearchIcon />} disabled={searching || query.trim().length < 3}>
                {searching ? 'Finding…' : 'Find'}
              </Button>
            </Stack>
            {results?.length > 0 && (
              <List dense sx={{ mt: 1, maxHeight: 180, overflowY: 'auto' }}>
                {results.map((place) => (
                  <ListItemButton key={place.key} onClick={() => goTo(place)}>
                    <ListItemText primary={place.name || place.label} secondary={`${place.label} · ${place.source}`} />
                  </ListItemButton>
                ))}
              </List>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: results?.length ? 0 : 0.5 }}>
              Moves the map only. Draw the fence yourself, because a search result lands near a site&apos;s
              entrance or office rather than on this stage.
            </Typography>
          </Paper>
          <ToggleButtonGroup exclusive size="small" value={mode} onChange={(event, value) => value && setMode(value)}>
            <ToggleButton value="CIRCLE">Circle</ToggleButton>
            <ToggleButton value="LANE">Lane or corridor</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary">
            {mode === 'LANE'
              ? 'Click along the middle of the lane to draw it, drag a point to move it, right-click a point to remove it. A truck within the width you set, either side of this line, is recorded at this stage.'
              : 'Drag the circle to move it and its handles to resize it. A circle suits a parking area or a customer site; for a lane between other lanes, draw the lane instead.'}
          </Typography>
          <Box ref={host} sx={{ height: 420, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }} />
          {!config?.googleMapsApiKey && <Alert severity="warning">Google Maps is not configured for this deployment, so a fence cannot be drawn here.</Alert>}
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            {mode === 'LANE' ? (
              <>
                <TextField
                  label="Width either side"
                  type="number"
                  size="small"
                  value={corridor}
                  onChange={(e) => setCorridor(e.target.value)}
                  inputProps={{ min: 3, max: 500 }}
                  helperText="Metres from the line"
                  sx={{ width: 180 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {points.length < 2 ? 'Click two or more points on the map to draw the lane.' : `${points.length} points · ${Math.round(laneLength)} m long · ${Math.round(corridor * 2)} m wide`}
                </Typography>
                <Button size="small" disabled={!points.length} onClick={() => setPoints((current) => current.slice(0, -1))}>Undo point</Button>
                <Button size="small" disabled={!points.length} onClick={() => setPoints([])}>Clear</Button>
              </>
            ) : (
              <TextField
                label="Radius"
                type="number"
                size="small"
                value={radius}
                onChange={(e) => { setRadius(e.target.value); shape.current?.setRadius(Number(e.target.value) || DEFAULT_RADIUS); }}
                inputProps={{ min: 10, max: 5000 }}
                helperText="Metres"
                sx={{ width: 160 }}
              />
            )}
          </Stack>
          <Alert severity="info">A fence takes effect immediately, including for trucks already on a trip through this stage.</Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pending || !ready || (mode === 'LANE' && points.length < 2)}
          onClick={save}
        >
          {pending ? 'Saving…' : 'Save fence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckpointFence;
